import { createCustomerPortalSession } from "@/api/privateRequests/stripe/createCustomerPortalSession";
import { CreditsNavChip } from "@/components/features/Credits/CreditsNavChip";
import { Button } from "@/design-system/Button/Button";
import { trackNavBarUpgrade } from "@/events/navBarMixpanel";
import { storeSignupContext } from "@/events/signupTracking";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { Subscription } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { creditsVisibleSelector } from "@repo/shared/redux";
import { CircleUserRound } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";

import { ContentLanguageDropdown } from "../ContentLanguagePicker";
import { NavPlayerSearch } from "../NavBarDesktop/components/NavPlayerSearch";

/** Search, credits, upgrade, profile and language: always on screen. */
export const AccountCluster = ({
  userIsLoggedIn,
  userSubscription,
  paidUserButNotUltimate,
  showSearch,
}: {
  userIsLoggedIn: boolean;
  userSubscription: Subscription;
  paidUserButNotUltimate: boolean;
  showSearch: boolean;
}) => {
  const { t } = useTranslation(["nav", "common"]);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const creditsVisible = useSelector(creditsVisibleSelector);

  const profileClass =
    "flex items-center gap-x-2 rounded px-2 py-1.5 text-sm font-medium whitespace-nowrap text-foreground-muted transition-colors hover:bg-surface hover:text-foreground";

  return (
    <div className="flex shrink-0 items-center gap-x-3">
      {showSearch && <NavPlayerSearch />}
      <CreditsNavChip />
      {userSubscription !== "diamond" && (
        <Button
          variant="primary"
          size="small"
          className="shrink-0 whitespace-nowrap"
          onClick={() => {
            trackNavBarUpgrade();
            if (paidUserButNotUltimate) {
              createCustomerPortalSession();
              return;
            }
            navigate("/subscription");
          }}
        >
          {creditsVisible
            ? t("common:getUnlimited")
            : paidUserButNotUltimate
              ? t("common:getUltimate")
              : t("common:upgrade")}
        </Button>
      )}
      {userIsLoggedIn ? (
        <Link to="/profile" className={profileClass}>
          <CircleUserRound className="size-5" strokeWidth={1.5} />
          <span data-squeeze="2">{t("common:profile")}</span>
        </Link>
      ) : (
        <button
          type="button"
          className={profileClass}
          onClick={(e) => {
            // Else the modal's outside-click listener catches this same click.
            e.stopPropagation();
            storeSignupContext("cta", { section: "Nav Bar" });
            dispatch(showModal({ modalType: "login" }));
          }}
        >
          {t("common:logIn")}
        </button>
      )}
      <ContentLanguageDropdown userIsLoggedIn={userIsLoggedIn} />
    </div>
  );
};
